import { FileElement, TedivoForm, translateTedivoForm } from "@tedivo/tedivo-form";
import { IFile, IVesselMasterDataExtended } from "@tedivo/tvd-api-models";
import { IntegratedDialog, IntegratedDialogError } from "@tedivo/tedivo-ui";
import { createFormFields, createFormValidator } from "./importNsdFields";
import { getFilenameParts, sanitizeString } from "@tedivo/tedivo-pure-helpers";
import { getTranslation, i18nReactive } from "../../../app/i18/i18tn";

import { I18nComponentRegisterer } from "@tedivo/tedivo-i18";
import ICustomError from "../../types/ICustomError";
import { IViewHistoryState } from "../types/IViewHistoryState";
import { ImoFoundResultEnum } from "../import-staf/ImoFoundResultEnum";
import LenUnitsEnum from "../../../app/enums/LenUnitsEnum";
import Services from "../../../app/services";
import SlButton from "@shoelace-style/shoelace/dist/components/button/button";
import SlInput from "@shoelace-style/shoelace/dist/components/input/input";
import { checkNsd } from "@tedivo/nsd-check";
import { createFoundImoDialog } from "../import-staf/createFoundImoDialog";
import { createNotFoundImoDialog } from "../import-staf/createNotFoundImoDialog";
import { focusAndSelect } from "../import-staf/focusAndSelect";
import globalStore from "../../../app/stores/globalStore";
import globalUnits from "../../../app/units/globalUnits";
import goSquared from "../../../app/tracking/goSquared";
import { routeFns } from "../../../app/router/routes";
import router from "../../../app/router";
import securityModule from "../../../app/security/SecurityModule";
import { setAppTitle } from "../../../app/setAppTitle";
import { splitNameForMainNames } from "../../../app/services/import-staf/stafFileConvertToOVD";
import topMessageElement from "../../layout/top-tools/getTopMessageElement";

/**
 * Import a Navis .nsd file as a new OVD.
 *
 * Modelled on the STAF import: `checkNsd` reads the file header here to find out
 * whether the LCGs are known and where they are referenced from, and LBP/LOA are
 * asked for only when they are needed to shift them to the aft perpendicular.
 * The conversion itself is done by the converter Lambda, from the view-json
 * component, once this form has been submitted.
 */
export class TVDImportNsdComponent extends HTMLElement {
  public static observedAttributes = [];

  private i18nCR: I18nComponentRegisterer;
  private dialogError: IntegratedDialogError = new IntegratedDialogError(
    this,
    false,
    getTranslation("general:common.close"),
  );
  private submitButton: SlButton | null = null;

  private ownFiles: { lastUpdated: number; list: IFile[] } = {
    lastUpdated: 0,
    list: [],
  };

  private shipFieldsState: TShipFieldsState = {
    formWithLpp: false,
    name: "",
    shipName: "",
    shipClass: "",
    vmdShipNameLatest: undefined,
  };

  private readyToSubmit = false;

  /**
   * The selected NSD file.
   *
   * Held on the component because `showOtherFields` calls `tedivoForm.setFields()`,
   * which rebuilds every control. A file input's value cannot be restored
   * programmatically (`FileElement.value` setter is a no-op), so the File would
   * otherwise be lost between selection and submit.
   */
  private selectedFile: File | undefined = undefined;

  private internalMatch: IVesselMasterDataExtended | undefined = undefined;

  private imoApiState: ImoFoundResultEnum = ImoFoundResultEnum.INITIALIZED;

  constructor() {
    super();
    this.i18nCR = new I18nComponentRegisterer(i18nReactive);

    setAppTitle(getTranslation("menu:fileImportNsd"));
  }

  connectedCallback() {
    // The converter Lambda rejects nsd2ovd for organizations without the flag,
    // so the page is pointless (and misleading) for them.
    if (securityModule.planIsReadOnly || !securityModule.currentOrganizationNsdToOvdAllowed) {
      router.navigate(routeFns.myCloud());
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const me = this;
    goSquared.trackPage("Import-NSD");
    goSquared.addEvent("Import-NSD - Show page");

    const wrapper = document.createElement("div"),
      h1 = document.createElement("h1");

    const box = document.createElement("div");
    box.className = "sections-oss-card";

    const submitButton = document.createElement("sl-button");
    submitButton.type = "submit";
    submitButton.innerHTML = getTranslation("general:common.submit");
    submitButton.variant = "primary";
    submitButton.disabled = true;
    this.submitButton = submitButton;

    const nextButton = document.createElement("sl-button");
    nextButton.type = "button";
    nextButton.innerHTML = getTranslation("general:common.next");
    nextButton.outline = true;
    nextButton.variant = "primary";

    const tedivoForm = new TedivoForm<INsdImportFields>({
      fields: createFormFields({ imoCode: "" }),
      onSubmit: this.onSubmitDataForm,
      formValidator: createFormValidator(),
      submitButton: submitButton,
    });

    const loadingBlocker = document.createElement("loading-blocker-element");
    this.appendChild(loadingBlocker);

    translateTedivoForm<INsdImportFields>({
      tedivoForm,
      i18nCR: this.i18nCR,
    });

    tedivoForm.onDataChange = async (values: INsdImportFields, name?: keyof INsdImportFields) => {
      globalStore.touchLastUserInteraction();

      switch (name) {
        case "fileInput": {
          const fileInput = values.fileInput;
          if (fileInput === undefined) return;

          this.selectedFile = fileInput as File;

          const fnameParts = getFilenameParts((fileInput as File).name);

          this.shipFieldsState.name = fnameParts.filename;

          this.shipFieldsState.formWithLpp = false;

          // The converter reads the vessel identity from the filename, never from
          // the names embedded in the .nsd, so an extension-less name is rejected
          // here rather than producing a nameless vessel later on.
          if (fnameParts.name === "") {
            this.showError(undefined, "general:common.attention", "errors:fileNameInvalid");
            (tedivoForm.getFormControlsByName().fileInput.field as FileElement).reset();
            this.selectedFile = undefined;
            return;
          }

          try {
            // Header-only read, in a tiny wasm module: it says whether the LCGs
            // were entered and what they are referenced to. The LBP is only asked
            // for when the converter will actually need it to shift them to the
            // aft perpendicular.
            const nsdCheck = checkNsd(await (fileInput as File).arrayBuffer());

            this.shipFieldsState.formWithLpp = nsdCheck.lcgKnown && !nsdCheck.isAftPP;

            console.info(
              `"${this.shipFieldsState.name}" NSD file summary:\nLCGs: ${
                nsdCheck.lcgKnown ? "known" : "estimated"
              } / LCG Ref: ${nsdCheck.lcgReference}`,
            );
          } catch (e) {
            this.showError(e as ICustomError, "general:common.attention", "errors:notValidNsdFile");
            (tedivoForm.getFormControlsByName().fileInput.field as FileElement).reset();
            this.selectedFile = undefined;
            return;
          }

          // A file picked after the IMO was confirmed must refresh the fields
          // derived from it; this is a no-op before that point.
          showOtherFields(this.shipFieldsState, this.i18nCR, this.imoApiState);

          if (this.internalMatch) {
            const match = this.internalMatch;
            tedivoForm
              .setValue("shipName", match.SHIPNAME)
              .setValue("callSign", match.CALLSIGN || "N/A")
              .setValue("yearBuilt", Number(match.BUILT ?? 0));
          }

          break;
        }
        case "imoCode": {
          if (!values.imoCode) break;

          loadingBlocker.show();

          const [nameInfo, searchResults] = await Promise.all([
            Services.vesselsMasterData.searchVesselByImoCode(values.imoCode),
            Services.vesselSearch.search(`'${values.imoCode}'`, ["imoCode"]),
          ]);

          const tvlResults =
            searchResults?.data?.filter(
              (r) => r.item.organizationId !== securityModule.currentOrganizationId,
            ) || [];

          loadingBlocker.hide();

          if (nameInfo?.data?.source === "error") {
            if (tedivoForm.execValidation(["imoCode", "fileInput"]).success) {
              this.imoApiState = ImoFoundResultEnum.NOT_FOUND_CONTINUE;
              showOtherFields(this.shipFieldsState, this.i18nCR, this.imoApiState);
            }
          }

          const match = nameInfo?.data?.match;
          this.internalMatch = match;

          if (!match) {
            createNotFoundImoDialog({
              parentNode: this,
              imoCode: values.imoCode || "",
              onButtonClicked: (btnSelected): void => {
                switch (btnSelected) {
                  case "continue":
                    this.imoApiState = ImoFoundResultEnum.NOT_FOUND_CONTINUE;
                    break;
                  case "fix":
                    this.imoApiState = ImoFoundResultEnum.NOT_FOUND_FIX;
                    setTimeout(() => {
                      focusAndSelect(tedivoForm.getFormControlsByName().imoCode.field as SlInput);
                    }, 150);
                    break;
                }

                if (tedivoForm.execValidation(["imoCode", "fileInput"]).success) {
                  showOtherFields(this.shipFieldsState, this.i18nCR, this.imoApiState);
                }
              },
            });
          } else {
            createFoundImoDialog({
              parentNode: this,
              imoCode: values.imoCode || "",
              btnSeeTvlSearchResults: tvlResults.length > 0,
              lastFetched: String(match.modifiedAt),
              shipName: match.SHIPNAME,
              onButtonClicked: (btnSelected): void => {
                switch (btnSelected) {
                  case "continue":
                    this.imoApiState = ImoFoundResultEnum.FOUND_CONTINUE;
                    this.shipFieldsState.shipName = match.SHIPNAME;
                    this.shipFieldsState.vmdShipNameLatest = match.SHIPNAME;
                    this.shipFieldsState.lppFromApi =
                      Number(match.mtParticularsJson?.LENGTH_B_W_PERPENDICULARS || 0) * 1000;
                    this.shipFieldsState.loaFromApi =
                      Number(match.mtParticularsJson?.LENGTH_OVERALL || 0) * 1000;

                    // Show
                    if (tedivoForm.execValidation(["imoCode", "fileInput"]).success) {
                      showOtherFields(this.shipFieldsState, this.i18nCR, this.imoApiState);
                    }

                    // Set fields
                    tedivoForm
                      .setValue("shipName", match.SHIPNAME)
                      .setValue("callSign", match.CALLSIGN || "N/A")
                      .setValue("yearBuilt", Number(match.BUILT ?? 0))
                      .setValue("lpp", this.shipFieldsState.lppFromApi)
                      .setValue("loa", this.shipFieldsState.loaFromApi);

                    break;
                  case "fix":
                    this.imoApiState = ImoFoundResultEnum.FOUND_FIX_IMO;
                    if (tedivoForm.execValidation(["imoCode", "fileInput"]).success) {
                      showOtherFields(this.shipFieldsState, this.i18nCR, this.imoApiState);
                    }

                    focusAndSelect(tedivoForm.getFormControlsByName().imoCode.field as SlInput);

                    break;
                  case "doubleCheck":
                    this.imoApiState = ImoFoundResultEnum.FOUND_MANUAL_SET;

                    loadingBlocker.show();

                    Services.vesselsMasterData
                      .searchVesselByImoCode(values.imoCode || "", true)
                      .then((doucleCheckNameInfo) => {
                        loadingBlocker.hide();
                        const newMatch = doucleCheckNameInfo?.data?.match;
                        if (newMatch) {
                          this.shipFieldsState.shipName = newMatch.SHIPNAME;
                          this.shipFieldsState.vmdShipNameLatest = match.SHIPNAME;
                          tedivoForm.setValue("shipName", newMatch.SHIPNAME);
                        }
                      })
                      .catch(() => {
                        loadingBlocker.hide();
                      });
                    break;
                  case "seeSearchResults":
                    router.navigate(routeFns.communityFiles(), {
                      searchText: values.imoCode,
                      fields: ["imoCode"],
                      data: searchResults.data,
                    });
                    break;
                }
              },
            });
          }

          break;
        }
      }

      function showOtherFields(
        shipFieldsState: TShipFieldsState,
        i18nCR: I18nComponentRegisterer,
        imoApiState: ImoFoundResultEnum,
      ) {
        const { formWithLpp, name, shipClass, shipName, lppFromApi, loaFromApi } = shipFieldsState;

        const doContinue =
          imoApiState === ImoFoundResultEnum.FOUND_CONTINUE ||
          imoApiState === ImoFoundResultEnum.FOUND_MANUAL_SET ||
          imoApiState === ImoFoundResultEnum.NOT_FOUND_CONTINUE;

        if (!doContinue) return;

        me.readyToSubmit = true;

        const nameFields = splitNameForMainNames(name || "", shipClass || "");
        if (shipName) nameFields.shipName = shipName;

        const newFields = createFormFields({
          imoCode: values.imoCode || "",
          fileLabel: name,
          lppFromApi,
          loaFromApi,
          withLpp: formWithLpp,
          withNameFields: nameFields,
        });

        tedivoForm.setFields(newFields).setFormValidator(
          createFormValidator({
            withLpp: formWithLpp,
            withNameFields: true,
            // setFields() rebuilds the file control; the File itself lives on the component
            fileAlreadyLoaded: me.selectedFile !== undefined,
          }),
        );

        const divButtons = document.createElement("div");
        divButtons.className = "form-buttons";
        divButtons.appendChild(submitButton);
        tedivoForm.form.appendChild(divButtons);

        translateTedivoForm<INsdImportFields>({
          tedivoForm,
          i18nCR,
        });

        submitButton.disabled = false;
      }
    };

    box.appendChild(tedivoForm.form);

    const divButtons = document.createElement("div");
    divButtons.className = "form-buttons";
    divButtons.appendChild(nextButton);
    tedivoForm.form.appendChild(divButtons);

    wrapper.appendChild(h1);
    wrapper.appendChild(box);
    this.appendChild(wrapper);

    // Translations
    const i18nCR = this.i18nCR;

    i18nCR.addConsumer(h1, "menu:fileImportNsd", "innerHTML");
    i18nCR.addConsumer(topMessageElement.element, "menu:fileImportNsd", "innerHTML");

    nextButton.addEventListener("click", () => {
      tedivoForm.onDataChange?.(tedivoForm.getValues(), "imoCode");
    });

    document.documentElement.addEventListener("globalUnitsChanged", () => {
      const helpText = `enums:LenUnitsEnum.${LenUnitsEnum[globalUnits.lengthUnits.units]}`;
      const controls = tedivoForm.getFormControlsByName();

      if (controls.lpp) i18nCR.addConsumer(controls.lpp.field as HTMLElement, helpText, "helpText");
      if (controls.loa) i18nCR.addConsumer(controls.loa.field as HTMLElement, helpText, "helpText");
    });
  }

  disconnectedCallback() {
    this.i18nCR.disconnect();
  }

  onSubmitDataForm = async (values: INsdImportFields) => {
    globalStore.touchLastUserInteraction();

    if (!this.readyToSubmit) return;

    if (this.submitButton) {
      this.submitButton.loading = true;
      this.submitButton.disabled = true;
    }

    const file = this.selectedFile;

    if (file === undefined) {
      this.showError(undefined, "general:common.attention", "errors:notValidNsdFile");
      if (this.submitButton) {
        this.submitButton.loading = false;
        this.submitButton.disabled = false;
      }
      return;
    }

    const filenameParts = getFilenameParts(file.name);
    const name = sanitizeString(filenameParts.name);

    const viewState: IViewHistoryState = {
      file,
      filenameParts,
      source: "nsdFile",
      vmdShipNameLatest: this.shipFieldsState.vmdShipNameLatest,
      formFields: {
        lpp: Number((values.lpp || this.shipFieldsState.lppFromApi) ?? 0),
        loa: Number((values.loa || this.shipFieldsState.loaFromApi) ?? 0),
        shipClass: values.shipClass || "",
        shipName: values.shipName || "",
        lineOperator: values.lineOperator || "",
        imoCode: values.imoCode || "",
        callSign: values.callSign,
        yearBuilt: values.yearBuilt,
      },
    };

    await this.getOwnFiles();

    if (this.ownFiles.list.some(({ name }) => name === values.shipName)) {
      const modal = new IntegratedDialog<"cancel" | "saveAnyway">({
        parentNode: this,
        preventDefaultClose: true,
        preventClose: true,
        buttonsAttrs: [
          {
            label: getTranslation("general:common.saveAnyway"),
            value: "saveAnyway",
            variant: "danger",
            icon: "exclamation-triangle",
            type: "submit",
          },
          {
            label: getTranslation("general:common.cancel"),
            value: "cancel",
            icon: "x-lg",
            type: "button",
            autofocus: true,
          },
        ],
        removeDialogWhenHidden: true,
      });

      modal.show(
        getTranslation("general:common.attention"),
        getTranslation("errors:fileWithSameNameExists"),
      );

      modal.onButtonClicked = (btnSelected) => {
        if (btnSelected === "saveAnyway") {
          goSquared.addEvent("Import-NSD - Exec");
          router.navigate(routeFns.ovdEdit(name), viewState);
        }
      };
    } else {
      goSquared.addEvent("Import-NSD - Exec");
      router.navigate(routeFns.ovdEdit(name), viewState);
    }

    if (this.submitButton) {
      this.submitButton.loading = false;
      this.submitButton.disabled = false;
    }
  };

  private showError = (
    e: ICustomError | undefined,
    errorTlt = "errors:errorHasOcurred",
    errorStr = "errors:notValidNsdFile",
  ) => {
    this.dialogError.show(getTranslation(errorTlt), getTranslation(errorStr));
    if (e !== undefined) console.error(e);
  };

  private async getOwnFiles() {
    const lastUpdated = this.ownFiles.lastUpdated;

    //Fetch if first-time or cache is > 5s
    if (lastUpdated === 0 || Date.now() - lastUpdated > 5000) {
      const data = await Services.files.getAll();
      if (data.statusCode === 200 && data.data !== undefined) {
        this.ownFiles.list = data.data;
        this.ownFiles.lastUpdated = Date.now();
      }
    }
  }
}

customElements.define("tvd-import-nsd-component", TVDImportNsdComponent);

declare global {
  interface HTMLElementTagNameMap {
    "tvd-import-nsd-component": TVDImportNsdComponent;
  }
}

export interface INsdImportFields extends Record<string, unknown> {
  fileInput?: File;
  lpp?: number;
  loa?: number;
  lineOperator?: string;
  shipClass?: string;
  shipName?: string;
  callSign?: string;
  yearBuilt?: number;
  imoCode?: string;
}

type TShipFieldsState = {
  /** The .nsd has known LCGs that are not aft-perpendicular referenced, so the
   *  converter needs the LBP to shift them. */
  formWithLpp: boolean;
  name?: string;
  shipName?: string;
  shipClass?: string;
  lppFromApi?: number;
  loaFromApi?: number;
  vmdShipNameLatest?: string;
};
